import { useState, useEffect } from 'react';

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import GitLabModal from './gitlab-modal';

interface GitLabButtonProps {
  projectId: string;
  projectSlug: string;
  gitlabConnection?: {
    connectionId: string;
    repoUrl: string;
    repoOwner: string;
    repoName: string;
    defaultBranch: string;
    syncEnabled: boolean;
  };
  onRefresh?: () => void;
  initialOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export default function GitLabButton({
  projectId,
  projectSlug,
  gitlabConnection,
  onRefresh,
  initialOpen = false,
  onOpenChange,
}: GitLabButtonProps) {
  const [isOpen, setIsOpen] = useState(initialOpen);

  useEffect(() => {
    if (initialOpen) {
      setIsOpen(true);
    }
  }, [initialOpen]);

  const handleSuccess = () => {
    if (onRefresh) {
      onRefresh();
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    onOpenChange?.(false);
  };

  const handleOpen = () => {
    setIsOpen(true);
    onOpenChange?.(true);
  };

  const isConnected = !!gitlabConnection;

  return (
    <>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              onClick={handleOpen}
              className={cn(
                "relative flex items-center justify-center h-7 w-7 rounded-full cursor-pointer",
                "bg-[#FC6D26] transition-all duration-200 ease-out",
                "hover:scale-125 hover:-translate-y-0.5 hover:z-50",
              )}
            >
              <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none">
                <path d="m23.6004 9.5927-.0337-.0862L20.3.9814a.851.851 0 0 0-.3362-.405.8748.8748 0 0 0-.9997.0539.8748.8748 0 0 0-.29.4399l-2.2055 6.748H7.5375l-2.2057-6.748a.8573.8573 0 0 0-.29-.4412.8748.8748 0 0 0-.9997-.0537.8585.8585 0 0 0-.3362.4049L.4332 9.5015l-.0325.0862a6.0657 6.0657 0 0 0 2.0119 7.0105l.0113.0087.03.0213 4.976 3.7264 2.462 1.8633 1.4994 1.1321a1.0085 1.0085 0 0 0 1.2197 0l1.4994-1.1321 2.4619-1.8633 5.006-3.7489.0125-.01a6.0682 6.0682 0 0 0 2.0094-7.003z" fill="white"/>
              </svg>
              {isConnected && (
                <span className="absolute -bottom-0.5 -right-0.5 h-2 w-2 rounded-full bg-green-500 ring-1 ring-background" />
              )}
            </button>
          </TooltipTrigger>
          <TooltipContent>
            <p>
              {isConnected
                ? `Connected: ${gitlabConnection.repoOwner}/${gitlabConnection.repoName}`
                : 'Connect to GitLab'}
            </p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <GitLabModal
        isOpen={isOpen}
        onClose={handleClose}
        onSuccess={handleSuccess}
        projectId={projectId}
        projectSlug={projectSlug}
        existingConnection={gitlabConnection}
      />
    </>
  );
}
